import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  InputGroup,
  Alert,
  Modal,
  ProgressBar,
} from "react-bootstrap";
import {
  Bell,
  BoxArrowRight,
  PersonCircle,
  Search,
  ArrowLeft,
  CheckCircleFill,
  CalendarEvent,
  Clock,
  XCircleFill,
  FileEarmarkText,
  PencilSquare,
  ChevronRight,
  TrashFill,
} from "react-bootstrap-icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import LOGO_URL from "../img/tmis_logo_orig.png";

const NAV_BLUE = "#183B72";
const PANEL_BG = "#F1F3F6";
const PANEL_SURFACE = "#E7EAEE";
const BRAND = "#3D74F5";

const API_BASE = process.env.REACT_APP_API || "";
const ENDPOINT_SUBMIT = `${API_BASE}/createDo`;
const ENDPOINT_UPDATE = `${API_BASE}/updateDo`;
const ENDPOINT_ACTIVITIES = `${API_BASE}/getActivitiesByUser`;
const ENDPOINT_UPLOAD = `${API_BASE}/uploadDocument`;
const ENDPOINT_ACTIVITY_BY_ID = `${API_BASE}/getActivityById`;
const ENDPOINT_LOGGEDIN = `${API_BASE}/getLoggedInDetails`;
const ENDPOINT_DELETE_ACTIVITY = `${API_BASE}/deleteActivity`;

const STATUS_STYLES = {
  Pending: {
    bg: "rgba(245,158,11,.14)",
    text: "#B45309",
    icon: <Clock size={14} />,
  },
  Approved: {
    bg: "rgba(16,185,129,.14)",
    text: "#047857",
    icon: <CheckCircleFill size={14} />,
  },
  Rejected: {
    bg: "rgba(239,68,68,.14)",
    text: "#B91C1C",
    icon: <XCircleFill size={14} />,
  },
  "Re-submit": {
    bg: "rgba(59,130,246,.14)",
    text: "#1D4ED8",
    icon: <Clock size={14} />,
  },
  Draft: {
    bg: "rgba(139,92,246,.14)",
    text: "#6D28D9",
    icon: <PencilSquare size={14} />,
  },
  Default: {
    bg: "rgba(148,163,184,.16)",
    text: "#475569",
    icon: <Clock size={14} />,
  },
};

const MAX_FILES = 3;
const MAX_FILE_MB = 15;
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.heic,.heif";

function statusPillStyle(s) {
  const { bg, text } = STATUS_STYLES[s] || STATUS_STYLES.Default;
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 10px",
    borderRadius: 999,
    background: bg,
    color: text,
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap",
  };
}

const cardWrap = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 16,
  padding: "16px 18px",
  boxShadow: "0 2px 10px rgba(2,6,23,.05)",
  transition: "transform .15s ease, box-shadow .15s ease, border-color .15s ease",
};

function TagChip({ icon, label, tone }) {
  const bg = tone === "good" ? "#ECFDF5" : tone === "bad" ? "#FEF2F2" : "#F1F5F9";
  const border = tone === "good" ? "#A7F3D0" : tone === "bad" ? "#FECACA" : "#E2E8F0";
  const color = tone === "good" ? "#047857" : tone === "bad" ? "#B91C1C" : "#334155";
  return (
    <span
      className="d-flex align-items-center"
      style={{
        gap: 6,
        fontSize: 12.5,
        fontWeight: 600,
        color,
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 999,
        padding: "4px 10px",
      }}
    >
      {icon}
      {label}
    </span>
  );
}

function DoHistoryCard({
  fromText,
  toText,
  status,
  didTour,
  canEdit,
  onClick,
  onDelete,
}) {
  const pill = STATUS_STYLES[status] || STATUS_STYLES.Default;
  const isDraft = String(status).toLowerCase() === "draft";

  const setFocusRing = (el, on) => {
    el.style.boxShadow = on
      ? "0 0 0 3px rgba(61,116,245,.35), 0 10px 26px rgba(2,6,23,.10)"
      : cardWrap.boxShadow;
  };

  return (
    <div
      style={{ ...cardWrap, marginBottom: 12, cursor: "pointer", outline: "none" }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      onFocus={(e) => setFocusRing(e.currentTarget, true)}
      onBlur={(e) => setFocusRing(e.currentTarget, false)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 10px 26px rgba(2,6,23,.10)";
        e.currentTarget.style.borderColor = "#cbd5e1";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "none";
        e.currentTarget.style.boxShadow = cardWrap.boxShadow;
        e.currentTarget.style.borderColor = "#e5e7eb";
      }}
    >
      <div
        className="d-flex align-items-center justify-content-between flex-wrap"
        style={{ gap: 8 }}
      >
        <div
          className="d-flex align-items-center"
          style={{ gap: 7, color: "#1F2937", fontSize: 13 }}
        >
          <CalendarEvent size={15} />
          <span style={{ fontWeight: 600 }}>
            {fromText || "—"} – {toText || "—"}
          </span>
        </div>

        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <span style={statusPillStyle(status)}>
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              {pill.icon}
              {status}
            </span>
          </span>

          {isDraft && (
            <button
              type="button"
              title="Delete draft"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.();
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: "50%",
                border: "1px solid #FECACA",
                background: "#FEF2F2",
                color: "#B91C1C",
                cursor: "pointer",
              }}
            >
              <TrashFill size={13} />
            </button>
          )}
        </div>
      </div>

      {typeof didTour === "boolean" && (
        <div className="d-flex flex-wrap mt-3" style={{ gap: 8 }}>
          <TagChip
            icon={
              didTour ? (
                <CheckCircleFill size={13} />
              ) : (
                <XCircleFill size={13} />
              )
            }
            label={didTour ? "Conducted Tour" : "No Tour"}
            tone={didTour ? "good" : "bad"}
          />
        </div>
      )}

      <div
        className="d-flex align-items-center justify-content-between mt-3 pt-2"
        style={{ borderTop: "1px dashed #e2e8f0" }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: canEdit ? "#2563EB" : "#64748b",
          }}
        >
          {canEdit
            ? "Click to update your submission"
            : "Click to view your DO submission(s)"}
        </span>
        <ChevronRight size={13} color={canEdit ? "#2563EB" : "#64748b"} />
      </div>
    </div>
  );
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const toYMD = (v) => {
  if (!v) return "";
  if (typeof v === "string" && /^\d{2}-\d{2}-\d{4}$/.test(v)) {
    const [dd, mm, yyyy] = v.split("-");
    const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

async function inferDoIdAfterCreate({
  endpoint,
  token,
  userId,
  dateFrom,
  dateTo,
  tries = 10,
  wait = 600,
}) {
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const y = new Date(dateFrom || dateTo || Date.now()).getFullYear();
  const fromKey = toYMD(dateFrom);
  const toKey = toYMD(dateTo);

  for (let i = 0; i < tries; i++) {
    const { data } = await axios.get(endpoint, {
      params: { module: "do", userId, isAdmin: false, year: y },
      headers,
      timeout: 20000,
    });

    const rows = Array.isArray(data?.data) ? data.data : [];
    const match =
      rows.find((r) => {
        const rFrom = toYMD(
          r.actDateFr || r.activity_date_from || r.dateFrom || r.date,
        );
        const rTo = toYMD(
          r.actDateTo || r.activity_date_to || r.dateTo || r.date,
        );
        return (!fromKey || rFrom === fromKey) && (!toKey || rTo === toKey);
      }) || rows[0];

    const found = match?.do_id || match?.id || null;
    if (found) return found;

    await sleep(wait);
  }
  return null;
}

const formatSize = (bytes = 0) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function ExistingDocsBox({
  title = "Existing documents",
  docs = [],
  formatDate,
}) {
  if (!docs?.length) return null;

  return (
    <div
      className="mt-2"
      style={{
        fontSize: 12,
        background: "#EFF6FF",
        borderRadius: 10,
        padding: "10px 12px",
        border: "1px solid rgba(37,99,235,.18)",
      }}
    >
      <div className="d-flex align-items-center gap-2 mb-1">
        <FileEarmarkText size={14} />
        <div className="fw-bold">{title}</div>
      </div>

      <ul className="mb-1" style={{ paddingLeft: 18 }}>
        {docs.map((d) => (
          <li key={d.doc_id ?? d.id}>
            <span className="text-truncate" title={d.file_name}>
              {d.file_name}
            </span>{" "}
            <span className="text-muted">({formatDate(d.uploaded_date)})</span>
            {String(d.isOldFile ?? d.is_old_file ?? d.is_old ?? "0") ===
              "1" && <span className="ms-2 badge bg-secondary">Old</span>}
          </li>
        ))}
      </ul>

      <div className="text-muted">You can add more files if needed.</div>
    </div>
  );
}

function FilePickerSection({
  title,
  helper,
  files = [],
  existingDocs = [],
  onPick,
  onRemove,
  selectLabel = "Select Files",
  formatDate,
}) {
  const inputRef = useRef(null);

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center">
        <div className="fw-bold">{title}</div>
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => inputRef.current?.click()}
          style={{ borderRadius: 999 }}
          type="button"
        >
          {selectLabel}
        </Button>
      </div>

      <div className="text-muted" style={{ fontSize: 12 }}>
        {helper}
      </div>

      <div className="text-muted mb-2" style={{ fontSize: 12 }}>
        {files.length} / {MAX_FILES} selected
      </div>

      <ExistingDocsBox
        title={`Existing ${title.toLowerCase()}`}
        docs={existingDocs}
        formatDate={formatDate}
      />

      <input
        ref={inputRef}
        type="file"
        className="d-none"
        multiple
        onChange={(e) => {
          onPick(e.target.files);
          e.target.value = "";
        }}
        accept={ACCEPT}
      />

      <div
        className="mt-2"
        style={{
          background: "#fff",
          borderRadius: 12,
          border: "2px dashed #c9ced7",
          padding: 10,
        }}
      >
        {files.length === 0 ? (
          <div
            className="text-muted"
            style={{ minHeight: 42, display: "flex", alignItems: "center" }}
          >
            No file selected
          </div>
        ) : (
          files.map((f, idx) => (
            <div
              key={`${f.name}-${idx}`}
              className="d-flex align-items-center justify-content-between py-2"
              style={{ borderTop: idx ? "1px dashed #e5e7eb" : "none" }}
            >
              <div
                className="text-truncate"
                title={f.name}
                style={{ maxWidth: "75%" }}
              >
                {f.name}{" "}
                {typeof f.size === "number" && (
                  <small className="text-muted">({formatSize(f.size)})</small>
                )}
              </div>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    const previewWin = window.open("about:blank", "_blank");
                    if (!previewWin) {
                      alert(
                        "Popup blocked. Please allow popups for this site to preview files.",
                      );
                      return;
                    }
                    previewWin.location.href = URL.createObjectURL(f);
                  }}
                  style={{
                    borderRadius: 999,
                    padding: "2px 10px",
                    lineHeight: 1.2,
                  }}
                  type="button"
                >
                  View
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => onRemove(idx)}
                  style={{
                    borderRadius: 999,
                    padding: "2px 10px",
                    lineHeight: 1.2,
                  }}
                  type="button"
                >
                  Remove
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ProgressModal({ show, pct, msg }) {
  return (
    <Modal show={show} centered backdrop="static" keyboard={false}>
      <Modal.Body className="p-4 custom-font">
        <div className="d-flex align-items-center gap-3 mb-3">
          <Spinner animation="border" size="sm" />
          <div className="fw-bold" style={{ color: "#0f172a" }}>
            Please wait…
          </div>
        </div>

        <div className="text-muted mb-2" style={{ fontSize: 13 }}>
          {msg || "Processing…"}
        </div>

        <ProgressBar now={pct} label={`${pct}%`} />
        {pct >= 100 && (
          <div
            className="mt-2"
            style={{ fontSize: 12, color: "#047857", fontWeight: 700 }}
          >
            Completed
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default function DoSubmission() {
  const [form, setForm] = useState({
    dateFrom: "",
    dateTo: "",
    remFiles: [],
    tourFiles: [],
    conductedTour: false,
  });
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [editingId, setEditingId] = useState(null);
  const [editingStatus, setEditingStatus] = useState("");
  const [showDraftConfirm, setShowDraftConfirm] = useState(false);
  const [wasDraftSave, setWasDraftSave] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [existingDocs, setExistingDocs] = useState({ rem: [], con: [] });
  const [editRemarks, setEditRemarks] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const [progressOpen, setProgressOpen] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [progressMsg, setProgressMsg] = useState("");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hisError, setHisError] = useState("");
  const [q, setQ] = useState("");

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const [year, setYear] = useState(String(currentYear));

  const [showSuccess, setShowSuccess] = useState(false);
  const [successId, setSuccessId] = useState(null);

  const FRONTEND_MIN_START = "2026-03-01";

  const [cycle, setCycle] = useState({
    start: FRONTEND_MIN_START,
    end: "",
    daysRemaining: null,
    eligible: true,
    learnReq: null,
    doReq: null,
  });

  const navigate = useNavigate();

  const token = useMemo(
    () =>
      localStorage.getItem("authToken") || localStorage.getItem("token") || "",
    [],
  );

  const { displayName, username } = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const dn =
        localStorage.getItem("display_name") ||
        user.display_name ||
        localStorage.getItem("username") ||
        "User Acc";
      const un =
        localStorage.getItem("username") ||
        user.username ||
        user.user_name ||
        dn ||
        "User";
      return { displayName: dn, username: un };
    } catch {
      const dn =
        localStorage.getItem("display_name") ||
        localStorage.getItem("username") ||
        "User Acc";
      return { displayName: dn, username: dn };
    }
  }, []);

  const userId = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      if (raw) {
        const u = JSON.parse(raw);
        const id =
          u?.user_id ??
          u?.id ??
          u?.userId ??
          u?.data?.user_id ??
          u?.data?.id ??
          u?.data?.userId ??
          "";
        if (id) return String(id);
      }
      const loose =
        localStorage.getItem("user_id") ||
        localStorage.getItem("userid") ||
        localStorage.getItem("uid") ||
        "";
      return loose ? String(loose) : "";
    } catch {
      return "";
    }
  }, []);

  const todayYMD = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(ENDPOINT_LOGGEDIN, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          timeout: 20000,
        });

        const obj = data?.data || {};
        let cycleStart = obj?.cycleStart || FRONTEND_MIN_START;
        let cycleEnd = obj?.cycleEnd || "";

        if (cycleStart < FRONTEND_MIN_START) cycleStart = FRONTEND_MIN_START;

        setCycle({
          start: cycleStart,
          end: cycleEnd,
          daysRemaining: obj?.daysRemaining ?? null,
          eligible: obj?.eligible ?? true,
          learnReq: obj?.noOfLearnReq ?? obj?.learnReq ?? null,
          doReq: obj?.noOfDoReq ?? obj?.doReq ?? null,
        });

        setForm((f) => {
          const next = { ...f };
          if (next.dateFrom && cycleStart && next.dateFrom < cycleStart)
            next.dateFrom = "";
          if (next.dateTo && cycleStart && next.dateTo < cycleStart)
            next.dateTo = "";
          if (next.dateFrom && cycleEnd && next.dateFrom > cycleEnd)
            next.dateFrom = "";
          if (next.dateTo && cycleEnd && next.dateTo > cycleEnd)
            next.dateTo = "";
          if (next.dateFrom && next.dateTo && next.dateTo < next.dateFrom)
            next.dateTo = "";
          return next;
        });
      } catch {
        setCycle((c) => ({ ...c, start: FRONTEND_MIN_START, end: todayYMD() }));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/", { replace: true });
  };

  const resetForm = () => {
    setForm({
      dateFrom: "",
      dateTo: "",
      remFiles: [],
      tourFiles: [],
      conductedTour: false,
    });
    setExistingDocs({ rem: [], con: [] });
    setEditingId(null);
    setEditingStatus("");
    setEditRemarks("");
  };

  const formatDate = (isoOrDMY) => {
    if (!isoOrDMY) return "-";
    if (typeof isoOrDMY === "string" && /^\d{2}-\d{2}-\d{4}$/.test(isoOrDMY)) {
      const [dd, mm, yyyy] = isoOrDMY.split("-");
      const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
    }
    const d = new Date(isoOrDMY);
    if (Number.isNaN(d.getTime())) return String(isoOrDMY);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const mergeFiles = (existingArr, pickedList) => {
    const current = Array.isArray(existingArr) ? existingArr : [];
    const picked = Array.from(pickedList || []);

    const tooBig = picked.find((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    if (tooBig) {
      setFormError(`"${tooBig.name}" exceeds ${MAX_FILE_MB} MB limit.`);
      return current;
    }

    const merged = [...current, ...picked].slice(0, MAX_FILES);
    if (current.length + picked.length > MAX_FILES) {
      setFormError(
        `Maximum ${MAX_FILES} files allowed per section. Extra files were ignored.`,
      );
    }
    return merged;
  };

  const onPickRem = (list) => {
    setFormError("");
    setField("remFiles", mergeFiles(form.remFiles, list));
  };
  const onPickTour = (list) => {
    setFormError("");
    setField("tourFiles", mergeFiles(form.tourFiles, list));
  };

  const removeAt = (section, idx) =>
    setField(
      section,
      form[section].filter((_, i) => i !== idx),
    );

  const hasRemFile =
    (form.remFiles?.length || 0) > 0 || (existingDocs.rem?.length || 0) > 0;

  const hasTourFile =
    (form.tourFiles?.length || 0) > 0 || (existingDocs.con?.length || 0) > 0;

  const validateForm = () => {
    if (!userId) return "Missing user id. Please login again.";
    if (!token) return "Missing auth token. Please login again.";
    if (!form.dateFrom) return "Please select Activity Date From.";
    if (!form.dateTo) return "Please select Activity Date To.";
    if (new Date(form.dateFrom) > new Date(form.dateTo))
      return "Invalid date range: From cannot be later than To.";

    if (!form.conductedTour)
      return "You must confirm that you conducted a tour for tourists.";

    if (!hasRemFile)
      return "Please upload at least one file for Proof of Remuneration.";

    if (!hasTourFile)
      return "Please upload at least one file for Proof of Tour Conducted.";

    const minStart = cycle.start || FRONTEND_MIN_START;
    const maxEnd = cycle.end;

    if (form.dateFrom < minStart || form.dateTo < minStart)
      return `Dates must be on/after ${minStart}.`;
    if (maxEnd && (form.dateFrom > maxEnd || form.dateTo > maxEnd))
      return `Dates must be on/before ${maxEnd}.`;

    return "";
  };

  // lighter validation for Save as Draft — skips the fields a draft is
  // allowed to leave incomplete (conducted tour, proof files)
  const validateDraftForm = () => {
    if (!userId) return "Missing user id. Please login again.";
    if (!token) return "Missing auth token. Please login again.";
    if (!form.dateFrom) return "Please select Activity Date From.";
    if (!form.dateTo) return "Please select Activity Date To.";
    if (new Date(form.dateFrom) > new Date(form.dateTo))
      return "Invalid date range: From cannot be later than To.";

    const minStart = cycle.start || FRONTEND_MIN_START;
    const maxEnd = cycle.end;

    if (form.dateFrom < minStart || form.dateTo < minStart)
      return `Dates must be on/after ${minStart}.`;
    if (maxEnd && (form.dateFrom > maxEnd || form.dateTo > maxEnd))
      return `Dates must be on/before ${maxEnd}.`;

    return "";
  };

  // ✅ IMPORTANT FIX:
  // Build deleteList from existingDocs so backend can mark isOldFile = 1 on resubmit.
  const buildDeleteListForType = (attachmentType) => {
    // backend groups by attachment_type
    const list =
      attachmentType === "remNoOfAttach"
        ? existingDocs.rem
        : attachmentType === "conNoOfAttach"
          ? existingDocs.con
          : [];

    // doc id field variations
    const ids = (Array.isArray(list) ? list : [])
      .map((d) => d?.doc_id ?? d?.docId ?? d?.id ?? d?.document_id)
      .filter(
        (v) =>
          v !== null && typeof v !== "undefined" && String(v).trim() !== "",
      );

    // unique
    return Array.from(new Set(ids.map((x) => String(x))));
  };

  async function uploadBucketsSequentially({
    doId,
    remFiles = [],
    tourFiles = [],
  }) {
    const jobs = [];
    if (remFiles.length) jobs.push({ type: "remNoOfAttach", files: remFiles });
    if (tourFiles.length)
      jobs.push({ type: "conNoOfAttach", files: tourFiles });
    if (!jobs.length) {
      // nothing new to upload (e.g. only existing files kept) — clean up
      // the progress modal that saveActivity already opened
      setProgressPct(100);
      setProgressMsg("Completed");
      await sleep(250);
      setProgressOpen(false);
      setProgressMsg("");
      setProgressPct(0);
      return;
    }

    const grandTotal = jobs.reduce(
      (sum, j) => sum + j.files.reduce((s, f) => s + (f.size || 0), 0),
      0,
    );
    let uploadedSoFar = 0;

    setProgressOpen(true);
    setProgressMsg(
      `Uploading ${jobs.reduce((n, j) => n + j.files.length, 0)} file(s)…`,
    );
    setProgressPct(0);

    for (let idx = 0; idx < jobs.length; idx++) {
      const { type, files } = jobs[idx];
      const displayIndex = idx + 1;
      const jobsLen = jobs.length;
      const baseUploaded = uploadedSoFar;
      const jobTotal = files.reduce((s, f) => s + (f.size || 0), 0);

      const fd = new FormData();

      fd.append("module", "do");
      fd.append("doId", String(doId));
      fd.append("attachmentType", type);
      fd.append("type", editingId ? "update" : "create");

      fd.append("name", displayName || username || "");
      fd.append("date", `${form.dateFrom} - ${form.dateTo}`);
      fd.append("typeOfActivity", "");
      fd.append("activityProvider", "");

      // ✅ FIX: during update, send all existing doc ids in that bucket
      const deleteIds = editingId ? buildDeleteListForType(type) : [];

      if (deleteIds.length) {
        deleteIds.forEach((docId, i) => {
          fd.append(`deleteList[${i}][docId]`, String(docId));
        });
      } else {
        // fallback so backend won't crash
        fd.append(`deleteList[0][docId]`, "-1");
      }

      for (const f of files) fd.append("files", f);

      const handleProgress = (evt) => {
        if (!evt?.total) return;
        const loadedInThisJob = Math.min(evt.loaded || 0, jobTotal);
        const combinedLoaded = baseUploaded + loadedInThisJob;
        const pct = Math.min(
          100,
          Math.round((combinedLoaded * 100) / Math.max(grandTotal, 1)),
        );
        setProgressPct(pct);
        setProgressMsg(`Uploading attachments (${displayIndex}/${jobsLen})…`);
      };

      await axios.post(ENDPOINT_UPLOAD, fd, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "x-userid": String(userId),
          "x-hid": String(doId),
          "x-fldrname": "do",
          "x-filenames": JSON.stringify([]),
        },
        timeout: 180000,
        onUploadProgress: handleProgress,
      });

      uploadedSoFar = baseUploaded + jobTotal;
      setProgressPct(
        Math.min(
          100,
          Math.round((uploadedSoFar * 100) / Math.max(grandTotal, 1)),
        ),
      );
    }

    setProgressMsg("All attachments uploaded");
    setProgressPct(100);
    await sleep(300);

    setProgressOpen(false);
    setProgressMsg("");
    setProgressPct(0);
  }

  const sanitizeYear = (val) => {
    const y = String(val || "")
      .replace(/[^\d]/g, "")
      .slice(0, 4);
    const yi = parseInt(y || "", 10);
    return Number.isFinite(yi) && yi >= 1900 ? yi : currentYear;
  };

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const params = {
        module: "do",
        userId,
        isAdmin: false,
        year: sanitizeYear(year),
      };
      const { data } = await axios.get(ENDPOINT_ACTIVITIES, {
        params,
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        timeout: 20000,
      });
      const rows = Array.isArray(data?.data) ? data.data : [];
      setItems(
        rows.map((r) => ({
          ...r,
          actDateFr:
            r.actDateFr || r.activity_date_from || r.dateFrom || r.date,
          actDateTo: r.actDateTo || r.activity_date_to || r.dateTo || r.date,
        })),
      );
      setHisError("");
    } catch (err) {
      setHisError(
        err?.response?.data?.message || "Unable to load history right now.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const filteredItems = items.filter((it) => {
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    const hay =
      `${it.actDateFr || ""} ${it.actDateTo || ""} ${it.status_name || ""}`.toLowerCase();
    return hay.includes(needle);
  });

  const isResubmitStatus = (status) => {
    const s = String(status || "").toLowerCase();
    return (
      s === "amendment" ||
      s === "re-submit" ||
      s === "resubmit" ||
      s === "re submit" ||
      s === "pending" ||
      s === "draft"
    );
  };

  const handleEditFromHistory = async (row) => {
    const status = row?.status_name || "";
    if (!isResubmitStatus(status)) return;

    const id = row.do_id || row.id;
    if (!id) return;

    try {
      setFormError("");
      setProgressOpen(false);
      setProgressPct(0);
      setProgressMsg("");

      const { data } = await axios.get(ENDPOINT_ACTIVITY_BY_ID, {
        params: { module: "do", id },
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        timeout: 20000,
      });

      const rows = Array.isArray(data?.data) ? data.data : data?.data || [];
      const r = Array.isArray(rows) ? rows[0] : rows;
      const docs = Array.isArray(data?.docs) ? data.docs : [];

      if (!r) {
        setFormError("Unable to load DO activity details.");
        return;
      }

      const dateFrom = toYMD(r.activity_date_from || r.actDateFr);
      const dateTo = toYMD(r.activity_date_to || r.actDateTo);

      const hasFlag =
        r.hasConductedTour ?? r.has_conducted_tour ?? r.tour_conducted ?? 0;
      const remarks =
        r.remarks || r.remark || r.comments || r.comment || row.remarks || "";

      setForm((prev) => ({
        ...prev,
        dateFrom: dateFrom || "",
        dateTo: dateTo || "",
        conductedTour: Number(hasFlag) === 1,
        remFiles: [],
        tourFiles: [],
      }));

      const grouped = { rem: [], con: [] };
      docs.forEach((d) => {
        if (d.attachment_type === "remNoOfAttach") grouped.rem.push(d);
        if (d.attachment_type === "conNoOfAttach") grouped.con.push(d);
      });
      setExistingDocs(grouped);

      setEditingId(id);
      setEditingStatus(status);
      setEditRemarks(remarks || "");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setFormError(
        err?.response?.data?.message ||
          "Unable to load DO activity details. Please try again.",
      );
    }
  };

  const cancelEdit = () => resetForm();

  const isSubmitDisabled =
    submitting ||
    !form.conductedTour ||
    !form.dateFrom ||
    !form.dateTo ||
    !hasRemFile ||
    !hasTourFile;

  // Draft can only be re-saved as Draft, or moved on to a full Submit.
  // A Pending / Re-submit item being edited can only be Submitted.
  const showDraftButton =
    !editingId || String(editingStatus).toLowerCase() === "draft";

  const saveActivity = async (statusName) => {
    setProgressMsg("");
    setProgressPct(0);
    setProgressOpen(false);

    const cntRem = (form.remFiles?.length || 0) + (existingDocs.rem?.length || 0);
    const cntCon = (form.tourFiles?.length || 0) + (existingDocs.con?.length || 0);
    const total = cntRem + cntCon;
    const hasConducted = Number(Boolean(form.conductedTour));
    const isUpdate = Boolean(editingId);

    const payload = {
      user_id: userId,
      dateFrom: form.dateFrom,
      dateTo: form.dateTo,
      hasConductedTour: hasConducted,
      remNoOfAttach: cntRem,
      conNoOfAttach: cntCon,
      statusName,
    };

    if (!isUpdate) payload.noOfAttached = total;
    else payload.doId = editingId;

    const endpoint = isUpdate ? ENDPOINT_UPDATE : ENDPOINT_SUBMIT;

    setSubmitting(true);
    setProgressOpen(true);
    setProgressPct(8);
    setProgressMsg(
      isUpdate ? "Updating DO submission…" : "Submitting DO activity…",
    );

    try {
      const { data } = await axios.post(endpoint, payload, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 30000,
      });

      if (!data?.success) throw new Error(data?.message || "Submit failed");

      setProgressPct(15);
      setProgressMsg("Finalizing submission…");

      let doIdForUpload = editingId || null;

      if (!isUpdate) {
        const newDoId = await inferDoIdAfterCreate({
          endpoint: ENDPOINT_ACTIVITIES,
          token,
          userId,
          dateFrom: form.dateFrom,
          dateTo: form.dateTo,
        });
        doIdForUpload = newDoId ?? null;
      }

      setSuccessId(doIdForUpload ?? null);

      // ✅ Upload (this is where old files become isOldFile=1)
      if (doIdForUpload && total > 0) {
        await uploadBucketsSequentially({
          doId: doIdForUpload,
          remFiles: form.remFiles,
          tourFiles: form.tourFiles,
        });
      } else {
        setProgressPct(100);
        setProgressMsg("Completed");
        await sleep(250);
        setProgressOpen(false);
        setProgressMsg("");
        setProgressPct(0);
      }

      setWasDraftSave(statusName === "Draft");
      setShowSuccess(true);
      resetForm();
      await fetchHistory();
    } catch (err2) {
      setProgressOpen(false);
      setProgressMsg("");
      setProgressPct(0);
      setFormError(
        err2?.response?.data?.message ||
          err2?.message ||
          "Failed to submit. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    const err = validateForm();
    if (err) {
      setFormError(err);
      return;
    }

    await saveActivity("Pending");
  };

  const handleClickSaveDraft = () => {
    setFormError("");
    const err = validateDraftForm();
    if (err) {
      setFormError(err);
      return;
    }
    setShowDraftConfirm(true);
  };

  const handleConfirmSaveDraft = async () => {
    setShowDraftConfirm(false);
    await saveActivity("Draft");
  };

  const handleRequestDeleteDraft = (it) => {
    setDeleteError("");
    setDeleteTarget(it);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDeleteDraft = async () => {
    const it = deleteTarget;
    if (!it) return;

    const actId = it.do_id || it.id;
    if (!actId) {
      setDeleteError("Unable to identify this draft.");
      return;
    }

    setDeleting(true);
    setDeleteError("");
    try {
      const { data } = await axios.delete(ENDPOINT_DELETE_ACTIVITY, {
        data: { module: "do", act_id: actId },
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 20000,
      });

      if (!data?.success)
        throw new Error(data?.message || "Failed to delete draft.");

      setShowDeleteConfirm(false);
      setDeleteTarget(null);

      if (
        editingId &&
        String(editingId) === String(actId) &&
        String(editingStatus).toLowerCase() === "draft"
      ) {
        resetForm();
      }

      await fetchHistory();
    } catch (e) {
      setDeleteError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to delete draft. Please try again.",
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="learn-wrap custom-font"
      style={{ background: PANEL_BG, minHeight: "100vh" }}
    >
      <ProgressModal show={progressOpen} pct={progressPct} msg={progressMsg} />

      <header
        className="learn-topbar"
        style={{ height: 80, background: "#fff" }}
      >
        <div className="container-xxl h-100 d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <img
              src={LOGO_URL}
              alt="TMIS"
              style={{ height: 50, objectFit: "contain" }}
            />
          </div>
          <div
            className="d-flex align-items-center gap-4"
            style={{ color: "#333" }}
          >
            <Bell size={20} color="#1A3584" />
            <BoxArrowRight
              size={20}
              color="#1A3584"
              style={{ cursor: "pointer" }}
              onClick={handleLogout}
              role="button"
            />
            <span
              style={{ cursor: "pointer", fontWeight: "bold" }}
              className="custom-font"
              onClick={() => navigate("/profile")}
            >
              {displayName}
            </span>
            <PersonCircle
              size={23}
              color="#1A3584"
              style={{ cursor: "pointer" }}
              onClick={() => navigate("/profile")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") navigate("/profile");
              }}
            />
          </div>
        </div>
      </header>

      <div
        style={{
          background: NAV_BLUE,
          color: "#fff",
          padding: "12px 0",
          boxShadow: "0 2px 0 rgba(0,0,0,0.04)",
        }}
      >
        <div
          className="container-xxl d-flex align-items-center"
          style={{ gap: 12 }}
        >
          <span
            role="button"
            aria-label="Go back"
            tabIndex={0}
            onClick={handleBack}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              cursor: "pointer",
            }}
          >
            <ArrowLeft size={18} color="#ffffff" />
          </span>
          <h4 className="m-0 fw-bold">Submission of DO activities</h4>
        </div>
      </div>

      <Container className="py-4">
        <Row className="g-4">
          <Col xs={12} lg={7}>
            <Card
              className="shadow-sm"
              style={{
                borderRadius: 18,
                border: "1px solid rgba(0,0,0,0.06)",
                background: PANEL_SURFACE,
              }}
            >
              <Card.Body className="p-4 p-md-5">
                <p className="text-danger small">
                  * Only activities from 1st March 2026 onwards will be accepted
                </p>

                {editingId && (
                  <Alert
                    variant="primary"
                    className="mb-3 d-flex justify-content-between align-items-start"
                  >
                    <div>
                      <div className="fw-bold mb-1">
                        Editing existing DO activity
                      </div>
                      <div style={{ fontSize: 13 }}>
                        You are updating a DO submission that requires amendment
                        / re-submit.
                      </div>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={cancelEdit}
                    >
                      Cancel edit
                    </Button>
                  </Alert>
                )}

                {editRemarks && (
                  <Alert variant="warning" className="mb-3">
                    <div className="fw-bold mb-1">Remarks from reviewer</div>
                    <div style={{ whiteSpace: "pre-wrap" }}>{editRemarks}</div>
                  </Alert>
                )}

                {formError && (
                  <Alert variant="danger" className="mb-3">
                    {formError}
                  </Alert>
                )}

                <Form onSubmit={onSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="dateFrom">
                        <Form.Label className="fw-bold mb-1">
                          Activity Date From
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={form.dateFrom}
                          onChange={(e) => {
                            const v = e.target.value;
                            setField("dateFrom", v);
                            if (form.dateTo && v && form.dateTo < v)
                              setField("dateTo", "");
                          }}
                          min={cycle.start || FRONTEND_MIN_START}
                          max={cycle.end || undefined}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="dateTo">
                        <Form.Label className="fw-bold mb-1">
                          Activity Date To
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={form.dateTo}
                          onChange={(e) => setField("dateTo", e.target.value)}
                          min={
                            form.dateFrom || cycle.start || FRONTEND_MIN_START
                          }
                          max={cycle.end || undefined}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <FilePickerSection
                    title="Supporting Document (Proof of Remuneration)"
                    helper={`(Maximum ${MAX_FILES} files allowed, ≤ ${MAX_FILE_MB} MB each)`}
                    files={form.remFiles}
                    existingDocs={existingDocs.rem}
                    onPick={onPickRem}
                    onRemove={(idx) => removeAt("remFiles", idx)}
                    selectLabel="Select Files"
                    formatDate={formatDate}
                  />

                  <FilePickerSection
                    title="Supporting Document (Proof of Tour Conducted)"
                    helper={`(Maximum ${MAX_FILES} files allowed, ≤ ${MAX_FILE_MB} MB each)`}
                    files={form.tourFiles}
                    existingDocs={existingDocs.con}
                    onPick={onPickTour}
                    onRemove={(idx) => removeAt("tourFiles", idx)}
                    selectLabel="Select Files"
                    formatDate={formatDate}
                  />

                  <Form.Group className="mb-2" controlId="conductedTour">
                    <Form.Check
                      type="checkbox"
                      label="I conducted a tour for tourists and have received remuneration"
                      checked={form.conductedTour}
                      onChange={(e) =>
                        setField("conductedTour", e.target.checked)
                      }
                      className="small"
                    />
                  </Form.Group>

                  <div className="text-center d-flex justify-content-center gap-2">
                    {showDraftButton && (
                      <Button
                        type="button"
                        variant="outline-secondary"
                        onClick={handleClickSaveDraft}
                        disabled={submitting}
                        style={{
                          minWidth: 160,
                          borderRadius: 20,
                        }}
                      >
                        Save as Draft
                      </Button>
                    )}
                    <Button
                      type="submit"
                      disabled={isSubmitDisabled}
                      style={{
                        background: isSubmitDisabled ? "#94A3B8" : BRAND,
                        border: "none",
                        minWidth: 160,
                        borderRadius: 20,
                        cursor: isSubmitDisabled ? "not-allowed" : "pointer",
                      }}
                    >
                      {submitting
                        ? editingId
                          ? "Updating..."
                          : "Submitting..."
                        : editingId
                          ? "Ready to Submit"
                          : "Submit"}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} lg={5}>
            <Card
              className="shadow-sm"
              style={{
                borderRadius: 18,
                border: "1px solid rgba(0,0,0,0.06)",
                background: PANEL_SURFACE,
              }}
            >
              <Card.Body className="p-3 p-md-4">
                <h5 className="fw-bold mb-3">History of DO activities</h5>

                <div className="d-flex gap-2 align-items-center mb-3">
                  <InputGroup>
                    <InputGroup.Text>
                      <Search size={16} />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                  </InputGroup>

                  <Form.Control
                    type="number"
                    min="1900"
                    max={currentYear}
                    placeholder="Year"
                    value={year}
                    onChange={(e) =>
                      setYear(e.target.value.replace(/[^\d]/g, "").slice(0, 4))
                    }
                    style={{ maxWidth: 120 }}
                    title="Filter by year"
                  />

                  <Button variant="primary" onClick={fetchHistory}>
                    Apply
                  </Button>
                </div>

                <div
                  style={{
                    maxHeight: 520,
                    overflowY: "auto",
                    border: "1px solid #eef0f3",
                    borderRadius: 12,
                    padding: 8,
                  }}
                >
                  {loading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" size="sm" /> Loading…
                    </div>
                  ) : hisError ? (
                    <div className="text-danger text-center py-4">
                      {hisError}
                    </div>
                  ) : filteredItems.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      No records found.
                    </div>
                  ) : (
                    filteredItems.map((it, i) => {
                      const fromText = formatDate(
                        it.activity_date_from ||
                          it.actDateFr ||
                          it.dateFrom ||
                          it.date,
                      );
                      const toText = formatDate(
                        it.activity_date_to ||
                          it.actDateTo ||
                          it.dateTo ||
                          it.date,
                      );
                      const status = it.status_name || "Pending";
                      const didTour =
                        typeof it.hasConductedTour !== "undefined"
                          ? Boolean(Number(it.hasConductedTour))
                          : typeof it.conducted_tour !== "undefined"
                            ? Boolean(Number(it.conducted_tour))
                            : undefined;

                      const canEdit = isResubmitStatus(status);

                      return (
                        <DoHistoryCard
                          key={it.id || it.do_id || i}
                          fromText={fromText}
                          toText={toText}
                          status={status}
                          didTour={didTour}
                          canEdit={canEdit}
                          onClick={() => {
                            if (canEdit) handleEditFromHistory(it);
                            else navigate("/profile");
                          }}
                          onDelete={() => handleRequestDeleteDraft(it)}
                        />
                      );
                    })
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal
        show={showSuccess}
        onHide={() => setShowSuccess(false)}
        centered
        backdrop="static"
        keyboard
      >
        <Modal.Body className="text-center p-4 custom-font">
          <div
            className="d-inline-flex align-items-center justify-content-center mb-3"
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: wasDraftSave
                ? "rgba(139,92,246,0.12)"
                : "rgba(16,185,129,0.12)",
            }}
          >
            {wasDraftSave ? (
              <PencilSquare size={40} color="#6D28D9" />
            ) : (
              <CheckCircleFill size={44} color="#10B981" />
            )}
          </div>
          <h5 className="fw-bold mb-2">
            {wasDraftSave
              ? "Draft Saved"
              : editingId
                ? "Update Successful"
                : "Submission Successful"}
          </h5>
          <div className="text-muted mb-3" style={{ lineHeight: 1.6 }}>
            {wasDraftSave ? (
              <>
                Your activity was saved as a draft. You can continue editing
                it anytime from your DO history below.
              </>
            ) : successId ? (
              <>Your DO activity was submitted.</>
            ) : (
              <>Your DO activity was submitted successfully.</>
            )}
          </div>
          <div className="d-flex justify-content-center gap-2">
            <Button
              onClick={() => {
                setShowSuccess(false);
                setWasDraftSave(false);
              }}
              style={{ background: BRAND, border: "none", minWidth: 120 }}
            >
              OK
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Save as Draft confirmation */}
      <Modal
        show={showDraftConfirm}
        onHide={() => setShowDraftConfirm(false)}
        centered
      >
        <Modal.Body className="text-center p-4 custom-font">
          <div
            className="d-inline-flex align-items-center justify-content-center mb-3"
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "rgba(139,92,246,0.12)",
            }}
          >
            <PencilSquare size={32} color="#6D28D9" />
          </div>
          <h5 className="fw-bold mb-2">Save as Draft?</h5>
          <div className="text-muted mb-3" style={{ lineHeight: 1.6 }}>
            This activity will be saved as a draft. It won't be reviewed
            until you come back and submit it.
          </div>
          <div className="d-flex justify-content-center gap-2">
            <Button
              variant="outline-secondary"
              onClick={() => setShowDraftConfirm(false)}
              disabled={submitting}
              style={{ minWidth: 100, borderRadius: 20 }}
            >
              No
            </Button>
            <Button
              onClick={handleConfirmSaveDraft}
              disabled={submitting}
              style={{
                background: "#6D28D9",
                border: "none",
                minWidth: 100,
                borderRadius: 20,
              }}
            >
              {submitting ? "Saving..." : "Yes"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Delete Draft confirmation */}
      <Modal
        show={showDeleteConfirm}
        onHide={() => {
          if (!deleting) {
            setShowDeleteConfirm(false);
            setDeleteTarget(null);
            setDeleteError("");
          }
        }}
        centered
      >
        <Modal.Body className="text-center p-4 custom-font">
          <div
            className="d-inline-flex align-items-center justify-content-center mb-3"
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "rgba(239,68,68,0.12)",
            }}
          >
            <TrashFill size={28} color="#B91C1C" />
          </div>
          <h5 className="fw-bold mb-2">Delete this draft?</h5>
          <div className="text-muted mb-3" style={{ lineHeight: 1.6 }}>
            This will permanently delete this draft DO activity and any
            files you've uploaded for it. This cannot be undone.
          </div>
          {deleteError && (
            <Alert variant="danger" className="text-start mb-3">
              {deleteError}
            </Alert>
          )}
          <div className="d-flex justify-content-center gap-2">
            <Button
              variant="outline-secondary"
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeleteTarget(null);
                setDeleteError("");
              }}
              disabled={deleting}
              style={{ minWidth: 100, borderRadius: 20 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDeleteDraft}
              disabled={deleting}
              style={{
                background: "#DC2626",
                border: "none",
                minWidth: 100,
                borderRadius: 20,
              }}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
